// AdminResponseSlaPage — /admin/response-sla：**响应时限的读面与裁决台**
// （SB-190 §7.3，第二百四十六批；03-scoring D4 ①，10 分）。
//
// ── 这一页回答的问题 ────────────────────────────────────────────────────────
// 「期间内要求具名当事人响应的每一件事项，是不是在 1 个工作日内得到了首次实质响应」。
// 时钟由后端读时现算（core/business_day）、候选由机器列，**「哪一条算实质」由人裁**——
// 这一页就是管理员落裁决的地方。承接方在 dc-cli `sla mine` 看到的行与这里逐字同形
// （设计 §0-12：证据的效力来自「双方都看得见且改不了」），所以这里不做任何归纳。
//
// ── 三条呈现纪律 ────────────────────────────────────────────────────────────
// ① **`needsRuling > 0` 时分数显示区间**（设计 §5.3）：一个把未裁事项藏起来的单一数字
//    不是读数。scoreRange 那一格只在这个守卫之下渲染（adminResponseSla.test 钉住）。
// ② **查询是显式动作**（按钮，不是边打字边查；AdminReadAuditPage 同款）：一次查询
//    对应一次可复述的取数动作。不给响应方 = 全部响应方，页面会标出来。
// ③ **裁决走对话框、按 kind 收窄字段、理由必填**：裁决是追加式台账里新的一行（没有改与
//    删——裁错了再落一条），不适用的字段绝不进载荷（服务端是拒不是忽略）。
//    唯一被渲染的人写文本是裁决人的 reason（纯文本）；反馈/站内信正文结构上不在报文里。
//
// 判据（查询参数、归一化、分数文本、表单形状、载荷）全在 pages/adminResponseSla.mjs
// 配单测；这里只渲染（SB-042：`.jsx` 里不放判据）。
import { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Paper, Stack, TableBody, TableCell, TableHead, TableRow, TextField,
  Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import AdminSubnav from '../components/AdminSubnav.jsx';
import AppSnackbar, { useNotify } from '../components/AppSnackbar.jsx';
import DataTable from '../components/DataTable.jsx';
import HelpLink from '../components/HelpLink.jsx';
import { fmtTime } from '../components/TicketDetailView.jsx';
import { useLoad } from '../hooks/useLoad.mjs';
import { useLang, useT } from '../i18n/index.mjs';
import {
  EXTEND_MAX_DAYS, RULING_ERR_KEYS, RULING_KINDS, candidateOptions, filterProblems, kindKey, normalizeReport,
  ruleKey, rulingFormState, rulingPayload, rulingProblems, scoreText, slaQuery, stateKey,
} from './adminResponseSla.mjs';

const EMPTY_FORM = { responders: '', from: '', to: '' };

export default function AdminResponseSlaPage() {
  const t = useT();
  const { lang } = useLang();
  const { snack, notify, clearSnack } = useNotify();
  const [form, setForm] = useState(EMPTY_FORM);
  // applied = 已按下「查询」的那份过滤器。null = 还没查过（初始不发请求）。
  const [applied, setApplied] = useState(null);

  const problems = filterProblems(form);
  const load = useLoad(
    () => (applied ? api.adminResponseSla(slaQuery(applied)) : Promise.resolve(null)),
    [applied],
  );
  const report = useMemo(() => normalizeReport(load.data), [load.data]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Box>
      <AdminSubnav />
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
        {t('admin.responseSla.title')}
        <HelpLink section="admin-response-sla" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('admin.responseSla.desc')}
      </Typography>

      <Stack direction="row" spacing={2} useFlexGap
             sx={{ alignItems: 'flex-start', flexWrap: 'wrap', mb: 2 }}>
        <TextField
          size="small"
          label={t('admin.responseSla.responders')}
          helperText={t('admin.responseSla.responders.helper')}
          value={form.responders}
          error={problems.includes('admin.responseSla.err.responders')}
          onChange={set('responders')}
          sx={{ minWidth: 260 }}
        />
        <TextField size="small" type="datetime-local" label={t('admin.responseSla.from')}
                   InputLabelProps={{ shrink: true }} value={form.from} onChange={set('from')} />
        <TextField size="small" type="datetime-local" label={t('admin.responseSla.to')}
                   InputLabelProps={{ shrink: true }} value={form.to} onChange={set('to')} />
        <Button variant="contained" disabled={problems.length > 0}
                onClick={() => setApplied({ ...form })}>
          {t('admin.responseSla.query')}
        </Button>
      </Stack>
      {problems.map((p) => (
        <Alert key={p} severity="warning" sx={{ mb: 2 }}>{t(p)}</Alert>
      ))}

      {applied === null ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('admin.responseSla.prompt')}</Typography>
        </Paper>
      ) : load.status === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : load.status === 'error' ? (
        <Box>
          <Alert severity="error" sx={{ mb: 1 }}>
            {t(errKey(load.error, { fallback: 'common.error.network' }))}
          </Alert>
          <Button size="small" onClick={load.reload}>{t('common.retry')}</Button>
        </Box>
      ) : (
        <Stack spacing={2}>
          {/* 摘要块。分数那一格由 scoreText 定形：待裁 > 0 ⇒ 区间。 */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={3} useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Stat label={t('admin.responseSla.summary.denominator')} value={report.denominator} />
              <Stat label={t('admin.responseSla.summary.numerator')} value={report.numerator} />
              <Stat label={t('admin.responseSla.summary.score')} value={scoreText(report)} strong />
              {report.needsRuling > 0 ? (
                // 区间只在有待裁事项时出现：单一数字会把未裁事项藏起来（设计 §5.3）。
                <Stat label={t('admin.responseSla.summary.scoreRange')}
                      value={`[${report.scoreRange[0]}, ${report.scoreRange[1]}]`} warn />
              ) : null}
              <Stat label={t('admin.responseSla.summary.needsRuling')} value={report.needsRuling}
                    warn={report.needsRuling > 0} />
              <Stat label={t('admin.responseSla.summary.pending')} value={report.pending} />
              <Stat label={t('admin.responseSla.summary.excluded')} value={report.excluded} />
              <Stat label={t('admin.responseSla.summary.integrity')} value={report.integrity}
                    warn={report.integrity > 0} />
            </Stack>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
              {t('admin.responseSla.calendar', {
                tz: report.calendar.tz || '—',
                holidays: report.calendar.holidays.length ? report.calendar.holidays.join(', ') : '—',
                threshold: report.calendar.threshold,
                from: report.from || '—',
                to: report.to || '—',
                responders: report.responders.length ? report.responders.join(', ') : t('admin.responseSla.responders.all'),
              })}
            </Typography>
          </Paper>

          {report.items.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('admin.responseSla.empty')}</Typography>
            </Paper>
          ) : (
            <DataTable minWidth={1100}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.responseSla.col.channel')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.responseSla.col.ref')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.responseSla.col.responder')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.responseSla.col.issuer')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.responseSla.col.markedAt')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.responseSla.col.deadline')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.responseSla.col.rule')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.responseSla.col.rank')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.responseSla.col.state')}</TableCell>
                  <TableCell>{t('admin.responseSla.col.candidates')}</TableCell>
                  <TableCell>{t('admin.responseSla.col.rulings')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('admin.responseSla.col.action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.items.map((row) => (
                  <ItemRow key={row.key} row={row} t={t} lang={lang} notify={notify} onRuled={load.reload} />
                ))}
              </TableBody>
            </DataTable>
          )}
        </Stack>
      )}
      <AppSnackbar snack={snack} onClose={clearSnack} />
    </Box>
  );
}

function Stat({ label, value, strong = false, warn = false }) {
  return (
    <Box sx={{ minWidth: 88 }}>
      <Typography variant="caption" color="text.secondary" component="div">{label}</Typography>
      <Typography variant={strong ? 'h6' : 'body1'}
                  color={warn ? 'warning.main' : 'text.primary'}
                  sx={{ fontWeight: strong ? 700 : 500 }}>
        {value}
      </Typography>
    </Box>
  );
}

const STATE_COLOR = {
  on_time: 'success', on_time_provisional: 'info', late: 'error',
  needs_ruling: 'warning', pending: 'default', excluded: 'default',
};

// 一行 = 一件事项。裁决走对话框（与 AdminEscalationsPage 的处置同形）：动作是追加式、
// 不可撤销的一行台账，且一张长表上同时敞着十几个理由框，人很容易把理由写进相邻那一行。
function ItemRow({ row, t, lang, notify, onRuled }) {
  const [open, setOpen] = useState(false);
  const sk = stateKey(row.state);
  const rk = ruleKey(row.rule);
  return (
    <TableRow>
      <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'ui-monospace, monospace' }}>{row.channel}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, monospace' }}>{row.ref}</Typography>
        <Typography variant="caption" color="text.secondary">#{row.itemRef}</Typography>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>#{row.responderId}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>#{row.issuerId}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Typography variant="body2">{fmtTime(row.markedAtMs, lang)}</Typography>
        <Typography variant="caption" color="text.secondary">{row.markedAt}</Typography>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Typography variant="body2">{fmtTime(row.effectiveDeadlineMs || row.deadlineMs, lang)}</Typography>
        <Typography variant="caption" color="text.secondary">
          {row.effectiveDeadlineAt || row.deadlineAt}
          {row.deadlineFrozen ? ` · ${t('admin.responseSla.frozen')}` : ''}
        </Typography>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{rk ? t(rk) : row.rule}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.sameDayRank}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {/* 值域外 ⇒ 渲染原串（不硬套标签：替读面说话比奇怪的词更坏）。 */}
        {sk ? <Chip size="small" variant="outlined" color={STATE_COLOR[row.state] ?? 'default'} label={t(sk)} />
            : <Typography variant="body2">{row.state}</Typography>}
        {row.witnessMismatch ? (
          <Typography variant="caption" color="error.main" component="div">
            {t('admin.responseSla.witnessMismatch')}
          </Typography>
        ) : null}
      </TableCell>
      <TableCell>
        {row.candidates.length === 0 ? '—' : row.candidates.map((c) => (
          <Typography key={c.key} variant="caption" component="div" sx={{ fontFamily: 'ui-monospace, monospace' }}>
            {c.key}{c.op ? ` ${c.op}` : ''}{c.strict ? ' ·strict' : ''} · {fmtTime(c.atMs, lang)}
          </Typography>
        ))}
      </TableCell>
      <TableCell sx={{ minWidth: 220 }}>
        {row.rulings.length === 0 ? '—' : row.rulings.map((r) => (
          <Box key={r.id} sx={{ mb: 0.5 }}>
            <Typography variant="caption" component="div">
              #{r.id} {kindKey(r.kind) ? t(kindKey(r.kind)) : r.kind}
              {r.candidateKind ? ` ← ${r.candidateKind}:${r.candidateId}` : ''}
              {r.extendDays ? ` +${r.extendDays} (${r.blockerRef})` : ''}
              {' · '}{r.actorKind}{r.actorId != null ? `#${r.actorId}` : ''} · {fmtTime(r.atMs, lang)}
            </Typography>
            {/* ⚠ 纯文本：裁决人自己的话。 */}
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{r.reason}</Typography>
          </Box>
        ))}
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Button size="small" variant="outlined" onClick={() => setOpen(true)}>
          {t('admin.responseSla.ruling.title')}
        </Button>
        {open ? (
          <RulingDialog row={row} t={t} notify={notify}
                        onClose={() => setOpen(false)}
                        onRuled={() => { setOpen(false); onRuled(); }} />
        ) : null}
      </TableCell>
    </TableRow>
  );
}

const EMPTY_RULING = { kind: '', candidate: '', promisedBy: '', extendDays: '', blockerRef: '', reason: '' };

function RulingDialog({ row, t, notify, onClose, onRuled }) {
  const [form, setForm] = useState(EMPTY_RULING);
  const [busy, setBusy] = useState(false);
  const shape = rulingFormState(form.kind);
  const { problems, canSubmit } = rulingProblems(form);
  const options = candidateOptions(row, form.kind);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setBusy(true);
    try {
      await api.adminAddResponseSlaRuling(rulingPayload(row, form));
      notify(t('admin.responseSla.ruling.done'), 'success');
      onRuled();
    } catch (e) {
      // RULE_VIOLATION 的服务端报文点名了病因，原样附在后面。
      const key = errKey(e, { byCode: RULING_ERR_KEYS, fallback: 'common.error.network' });
      notify(`${t(key)}${e?.message ? ` — ${e.message}` : ''}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('admin.responseSla.ruling.title')} · {row.channel} {row.ref} (#{row.itemRef})</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField select size="small" fullWidth label={t('admin.responseSla.ruling.kind')}
                     value={form.kind} onChange={set('kind')} disabled={busy}>
            {RULING_KINDS.map((k) => (
              <MenuItem key={k} value={k}>{t(`admin.responseSla.kind.${k}`)}</MenuItem>
            ))}
          </TextField>
          {shape?.candidate ? (
            <TextField select size="small" fullWidth label={t('admin.responseSla.ruling.candidate')}
                       value={form.candidate} onChange={set('candidate')} disabled={busy}
                       error={Boolean(problems.candidate)}
                       helperText={options.length === 0 ? t('admin.responseSla.ruling.noCandidate') : ''}>
              {options.map((c) => (
                <MenuItem key={c.key} value={c.key}>{c.key}{c.op ? ` ${c.op}` : ''}{c.strict ? ' ·strict' : ''} · {c.at}</MenuItem>
              ))}
            </TextField>
          ) : null}
          {shape?.promisedBy ? (
            <TextField size="small" type="datetime-local" fullWidth label={t('admin.responseSla.ruling.promisedBy')}
                       InputLabelProps={{ shrink: true }} value={form.promisedBy} onChange={set('promisedBy')}
                       disabled={busy} error={Boolean(problems.promisedBy)} />
          ) : null}
          {shape?.extend ? (
            <Stack direction="row" spacing={2}>
              <TextField size="small" type="number" label={t('admin.responseSla.ruling.extendDays')}
                         value={form.extendDays} onChange={set('extendDays')} disabled={busy}
                         error={Boolean(problems.extendDays)} slotProps={{ htmlInput: { min: 1, max: EXTEND_MAX_DAYS, step: 1 } }} />
              <TextField size="small" label={t('admin.responseSla.ruling.blockerRef')} fullWidth
                         value={form.blockerRef} onChange={set('blockerRef')} disabled={busy}
                         error={Boolean(problems.blockerRef)} />
            </Stack>
          ) : null}
          <TextField size="small" fullWidth multiline minRows={3} label={t('admin.responseSla.ruling.reason')}
                     /* helperText 而不是 placeholder：它要求的是一段有说法的话，而 placeholder
                        在人开始打字的一瞬间就消失了。 */
                     helperText={t('admin.responseSla.ruling.reasonHelper')}
                     value={form.reason} onChange={set('reason')} disabled={busy}
                     error={problems.reason === 'tooLong'} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        {/* 空理由（含全是空白的）按不动——服务端同样 400，这里是同一条判据的第二份。 */}
        <Button variant="contained" disabled={busy || !canSubmit} onClick={submit}>
          {t('admin.responseSla.ruling.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
